'use client';

import { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';

interface PriceSliderProps {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  currency: string; // e.g. PLN, EUR, USD, GBP – from API prices
}

export default function PriceSlider({ min, max, step, value, onChange, currency }: PriceSliderProps) {
  const [localValue, setLocalValue] = useState<[number, number]>(value);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleMinChange = (newMin: number) => {
    const clampedMin = Math.min(newMin, localValue[1]);
    const newValue: [number, number] = [clampedMin, localValue[1]];
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleMaxChange = (newMax: number) => {
    const clampedMax = Math.max(newMax, localValue[0]);
    const newValue: [number, number] = [localValue[0], clampedMax];
    setLocalValue(newValue);
    onChange(newValue);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const minPercent = ((localValue[0] - min) / (max - min)) * 100;
  const maxPercent = ((localValue[1] - min) / (max - min)) * 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-slate-500" />
          <span className="text-slate-700 dark:text-slate-300">Price Range ({currency})</span>
        </div>
        <div className="text-slate-900 dark:text-slate-100 font-semibold">
          {formatPrice(localValue[0])} - {formatPrice(localValue[1])}
        </div>
      </div>

      <div className="relative">
        {/* Track */}
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full relative">
          {/* Active Range */}
          <div
            className="absolute h-2 bg-blue-600 rounded-full"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
            }}
          />
        </div>

        {/* Min Slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[0]}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="absolute top-0 w-full h-2 opacity-0 cursor-pointer z-10"
          style={{ pointerEvents: localValue[0] === localValue[1] ? 'none' : 'auto' }}
        />

        {/* Max Slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[1]}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="absolute top-0 w-full h-2 opacity-0 cursor-pointer z-10"
        />

        {/* Thumbs */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-blue-600 rounded-full shadow-lg border-2 border-white dark:border-slate-800 cursor-pointer z-20"
          style={{ left: `calc(${minPercent}% - 8px)` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-blue-600 rounded-full shadow-lg border-2 border-white dark:border-slate-800 cursor-pointer z-20"
          style={{ left: `calc(${maxPercent}% - 8px)` }}
        />
      </div>

      {/* Input Fields */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={localValue[0]}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <span className="text-slate-500">-</span>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={localValue[1]}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

